import React, { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { EyeIcon } from 'src/assets/icons'
import { Button, Layout, MenuProps, Typography } from 'antd'
import { useLocation, useNavigate } from 'react-router-dom'
import { TABLE_NAME } from 'src/app/shared/constants/tableName.constants'

/* Localization TimeZone */
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import { EPermissions, ESubModules } from 'src/app/shared/models/roleAndPermission.model'
import { useRolePermission } from 'src/app/shared/hooks/useRolePermission'

import { useResultScreen } from './useResults'
import { StatusTag } from './StatusTag'
import CommonModal from 'src/app/shared/components/modal/common-modal'
import { colorBorder } from 'src/app/themes/primaryThemeDefault'
import EmailTemplate from './EmailTemplate'
import { menuUrls } from 'src/app/shared/constants/menu.constants'
import NewCommonTable from 'src/app/shared/components/common/new-table'
import { ShortenListTags } from 'src/app/shared/components/common/tag/ShortenListTags'
import { getContentDetailEmail } from 'src/app/shared/services/BackgroundService'
import { TableColumnDefs } from 'src/app/shared/components/common/new-table/table.model'
import { getEmailLogsDownloadPdf, getEmailLogsDownloadStatus } from 'src/app/shared/services/BusinessAdminLogsService'

dayjs.extend(utc)
dayjs.extend(timezone)
/* Localization TimeZone */

const SentEmailTextsResults: React.FC = () => {
  const { t } = useTranslation()
  const location = useLocation()
  const navigate = useNavigate()
  const { resultList, getResultList, isGettingList } = useResultScreen()

  const { userPermissions } = useRolePermission()
  const [isOpenDetailMail, setIsOpenDetailMail] = useState<boolean>(false)
  const [contentDetailEmail, setContentDetailEmail] = useState({})
  const [isLoadingDataDetailEmail, setIsLoadingDataDetailEmail] = useState<boolean>(false)
  useEffect(() => {
    if (!location?.state || !location?.state?.sentEmailLog) {
      navigate(menuUrls.sentEmailTexts)
    }
  }, [location, navigate])

  // handle modal detail mail
  const handleCloseDetailMail = () => {
    setIsOpenDetailMail(false)
  }
  const handleOpenDetailMail = () => {
    setIsOpenDetailMail(true)
  }

  const getItems = useCallback(
    (rowData: any): MenuProps['items'] => {
      return [
        userPermissions[ESubModules.PAYEE_MATCH_PARAM]?.includes(EPermissions.VIEW_ITEMS)
          ? {
              key: '1',
              label: (
                <Typography.Text style={{ fontSize: '14px' }}>
                  {t('SentEmailLog:sentEmailResults.actions.viewEmailDetails')}
                </Typography.Text>
              ),
              icon: <EyeIcon />,
              onClick: async () => {
                if (rowData?.id) {
                  handleOpenDetailMail()
                  try {
                    setIsLoadingDataDetailEmail(true)
                    const resDetailEmail = await getContentDetailEmail(rowData?.id)
                    setContentDetailEmail(resDetailEmail?.data)
                  } catch (e) {
                    console.log('ERROR: ', e)
                  } finally {
                    setIsLoadingDataDetailEmail(false)
                  }
                }
              },
            }
          : null,
      ]
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [t, navigate],
  )

  const handleDownload = useCallback(
    async (payload: any) => {
      return await getResultList(payload, true, location?.state?.sentEmailLog)
    },
    [getResultList, location?.state?.sentEmailLog],
  )
  const generateCustomColumnDefs = useCallback(
    (columnDefs: Array<TableColumnDefs>): Array<TableColumnDefs> =>
      columnDefs.map((item, index) => {
        const { colId } = item

        if (colId === 'status')
          return {
            ...item,
            dataType: 'date',
            cellRenderer: (props: any) => {
              const { data } = props
              return <StatusTag status={data.status} />
            },
          }

        if (colId === 'clientName') {
          return {
            ...item,
            width: 150,
            headerClass: 'custom-header-text',
            cellRenderer: (props: any) => {
              const { data } = props
              const columnWidth = props.eGridCell ? props.eGridCell.clientWidth : props.column.getActualWidth()
              return data.clientName ? <ShortenListTags data={data.clientName} width={columnWidth} /> : null
            },
          } as any
        }
        if (colId === 'accountName') {
          return {
            ...item,
            width: 150,
            headerClass: 'custom-header-text',
            cellRenderer: (props: any) => {
              const { data } = props
              const columnWidth = props.eGridCell ? props.eGridCell.clientWidth : props.column.getActualWidth()
              return data.accountName ? <ShortenListTags data={data.accountName} width={columnWidth} /> : null
            },
          } as any
        }

        return item
      }),
    [],
  )

  const handleSearch = useCallback(
    async (value: Partial<any>) => {
      await getResultList(value, false, location?.state?.sentEmailLog)
    },
    [getResultList, location?.state?.sentEmailLog],
  )

  return (
    <Layout.Content style={{ padding: 24 }}>
      <NewCommonTable
        rowData={resultList?.items}
        total={resultList?.total}
        displayAddBtn={false}
        translationNamespace='SentEmailLog:sentEmailResults.column'
        displayUploadButton={false}
        displayActionColumn={true}
        isGettingList={isGettingList}
        loading={isGettingList}
        pageName={TABLE_NAME.SENT_EMAIL_TEXTS_RESULTS}
        handleSearch={handleSearch}
        handleDownload={handleDownload}
        getDownloadStatus={getEmailLogsDownloadStatus}
        getDownloadFile={getEmailLogsDownloadPdf}
        actionDropdownItems={getItems}
        tableSettingName={TABLE_NAME.SENT_EMAIL_TEXTS_RESULTS}
        generateCustomColumnDefs={generateCustomColumnDefs}
      />
      <CommonModal
        title='Email Details'
        size='large'
        open={isOpenDetailMail}
        onClose={handleCloseDetailMail}
        footer={[
          <Button
            key='cancel-btn'
            style={{
              fontWeight: 600,
              width: '102px',
              height: '32px',
              border: `1px solid ${colorBorder}`,
            }}
            onClick={handleCloseDetailMail}
          >
            <Typography.Text>{t('UserRoles:close')}</Typography.Text>
          </Button>,
        ]}
      >
        <EmailTemplate data={contentDetailEmail} isLoading={isLoadingDataDetailEmail} />
      </CommonModal>
    </Layout.Content>
  )
}

export default SentEmailTextsResults
